import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTitle } from '@arema/components/Hooks';
import { Trans, useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { DeleteAssetModal, NoAccessMessage, NotFound, STPDownloadModal, UploadAssetButton, UserLog } from '../../components';
import { Button, Groupper, Header, Icon, Input, Label, Message, Table, useFrontier, Toolbar, Modal } from 'react-frontier2';
import { AssetFile, AssetType, BankAccount, BusinessBankType } from '../../AdminClasses';
import { addCommas, bindClick, dashify, fromUnixLocal, getNow, getNowLocal, groupBy, uniqueArray } from '@arema/components/Util';
import { DEBUG } from '../../AdminConfig';
import { SetLoading } from '@arema/components/Classes';
import { useUser } from '../../AdminHooks';
import API from '../../API';
import Validator from '@arema/components/Validator';
import UserAccess from '../../UserAccess';

interface PayoutGroup{
	group_id: number,
	bank_id: number,
	bank_name: string,
	bank_type: BusinessBankType,
	feedback: string,
	approved: boolean,
	deleted: boolean,
	user_creator: number,
	user_creator_name?: string,
	user_approved: number,
	user_approved_name?: string,
	user_deleted: number,
	user_deleted_name?: string,
	date_created: number,
	date_scheduled: number,
	date_deleted: number,
	date_approved: number,
	payouts: {
		payout_id: number,
		payout_type: number,
		payout_type_name: string,
		completed: boolean,
		approved: boolean,
		rejected: boolean,
		asset_count: number,
		cancelled: boolean,
		bank_account?: BankAccount,
		date_created: number,
		date_completed: number,
		dates: {
			date_id: number,
			event_id: number,
			event_name: string,
			date: number,
			amount: number,
		}[]
	}[]
}

interface GrouppedDeposit{
	account_id: number,
	email: string,
	bank_name: string,
	account_type: 'SPEI' | 'card',
	account_name: string,
	account_number: string,
	rfc: string,
	curp: string,
	zipcode: string,
	amount: number,
	payouts: {
		payout_id: number,
		event_name: string,
		amount: number,
	}[],
}

enum Modals{
	DEPOSIT_INFO = 1,
	PAYOUT_INFO = 2,
	CANCEL_GROUP = 3,
	PAYOUT_ASSETS = 4,
}

var PayoutGroupView = ()=>{
	var { t } = useTranslation();
	var { setTitle } = useTitle();
	var { id } = useParams<{ id: string }>();
	var { hasAccess } = useUser();
	var { Toast } = useFrontier();
	var [downloadPayouts, setDownloadPayouts] = useState<number[]>(null);
	var [group, setGroup] = useState<PayoutGroup>(null);
	var [loadError, setLoadError] = useState<string>(null);
	var [modalPrompts, setModalPrompts] = useState<string[]>(null);
	var [rejectFeedback, setRejectFeedback] = useState<string>(null);
	var [approveStatus, setApproveStatus] = useState<'approve' | 'reject'>(null);
	var [shownModal, setShownModal] = useState<Modals>(null);
	var [shownDeposit, setShownDeposit] = useState<number>(null);
	var [shownPayout, setShownPayout] = useState<number>(null);
	var [payoutAssets, setPayoutAssets] = useState<AssetFile[]>(null);
	var [deletingAsset, setDeletingAsset] = useState<AssetFile>(null);

	useEffect(()=>{
		if(!id || Number.isNaN(parseInt(id))) return;
		setTitle(t('payout_group.title', { id }));

		if(group && group.group_id==parseInt(id)) return;

		API.getPayoutGroup(parseInt(id)).then(res=>{
			if(res.error) return setLoadError(res.message);
			setGroup(res.data);
		}).catch(err=>{
			setLoadError(t('common.unexpected_code', { code: 'LCL-PYGRVW-LD-1' }));
		});
	}, []);
	
	var sendApproval = (setLoading: SetLoading)=>{
		if(!approveStatus) return;
		if(approveStatus==='reject'){
			var { valid, prompts } = Validator({ rejectFeedback }, {
				rejectFeedback: [{ rule: 'empty', label: t('payout_group.reject_reason') }]
			});
			setModalPrompts(prompts);
			if(!valid) return;
		}
		
		setModalPrompts(null);
		setLoading(true);
		var prom = approveStatus==='approve' ? API.approvePayoutGroup([group.group_id]) : API.rejectPayoutGroup([group.group_id], rejectFeedback)
		prom.then(res=>{
			if(res.error) return setModalPrompts([res.message]);
			var now = getNowLocal();
			if(approveStatus==='reject'){
				setGroup({
					...group,
					approved: false,
					date_approved: null,
					deleted: true,
					date_deleted: now.toSeconds(),
					feedback: rejectFeedback,
				});
			}else{
				setGroup({
					...group,
					approved: true,
					date_approved: now.toSeconds(),
					deleted: false,
					date_deleted: null,
					feedback: null
				});
			}
			setApproveStatus(null);
			Toast.success(t(`payout_group.${approveStatus}_toast`));
		}).catch(err=>{
			setModalPrompts([t('common.unexpected_code', { code: 'LCL-PYGRVW-APRV-1' })]);
		}).finally(()=>{
			setLoading(false);
		})
	}

	var showDeposit = (account_id: number)=>{
		return ()=>{
			setShownDeposit(account_id);
			setShownModal(Modals.DEPOSIT_INFO);
		}
	}

	var showPayout = (payout_id: number)=>{
		return ()=>{
			setShownPayout(payout_id);
			setShownModal(Modals.PAYOUT_INFO);
		}
	}

	var showAssets = (payout_id: number)=>{
		return ()=>{
			setShownPayout(payout_id);
			setPayoutAssets(null);
			setShownModal(Modals.PAYOUT_ASSETS);
			setModalPrompts(null);

			API.getPayoutAssets(payout_id).then(res=>{
				if(res.error) return setModalPrompts([res.message]);
				setPayoutAssets(res.data);
			}).catch(err=>{
				setModalPrompts([t('common.unexpected_code', { code: 'LCL-PYGRVW-SWAS-1' })]);
			});
		}
	}
	
	var showSTPDownload = (payouts: number[])=>{
		return ()=>{
			setDownloadPayouts(payouts);
		}
	}

	var onPayoutsCompleted = (payouts: number[])=>{
		if(!group) return;
		var new_payouts = [...group.payouts]
		for(var i of payouts){
			var px = new_payouts.findIndex(a=>a.payout_id===i);
			if(px!=-1){
				new_payouts[px].completed = true;
			}
		}
		setGroup({
			...group,
			payouts: new_payouts
		});
		Toast.success(t('payout_group.deposit_completed'))
	}

	var payoutsCompleted = (payouts: number[])=>{
		return (setLoading: SetLoading)=>{
			if(!hasAccess(UserAccess.CONTABILIDAD.SET_PAYOUT_COMPLETE)) return Toast.error(t('common.no_access_action'));
			setLoading(true);
			API.completePayouts(payouts).then(res=>{
				if(res.error) return setModalPrompts([res.message]);
				setShownModal(null);
				setShownDeposit(null);
				onPayoutsCompleted(payouts);
			}).catch(err=>{
				setModalPrompts([t('common.unexpected_code', { code: 'LCL-PYGRVW-DPCPL-1' })]);
			}).finally(()=>{
				setLoading(false);
			})
		}
	}

	var depositCompleted = (account_id: number)=>{
		return (setLoading: SetLoading)=>{
			var deposit = groupped_deposits.find(a=>a.account_id===account_id);
			if(!deposit || deposit.payouts.length==0) return;
			var payouts = deposit.payouts.map(a=>a.payout_id);
			return payoutsCompleted(payouts)(setLoading);
		}
	}

	var cancelGroup = (setLoading: SetLoading)=>{
		setModalPrompts(null);
		setLoading(true);
		API.cancelPayoutGroup(group.group_id).then(res=>{
			if(res.error) return setModalPrompts([res.message]);
			setGroup({
				...group,
				approved: false,
				date_approved: null,
				deleted: true,
				date_deleted: getNowLocal().toSeconds(),
				feedback: null,
			});
			setShownModal(null);
		}).catch(err=>{
			setModalPrompts([t('common.unexpected_code', { code: 'LCL-PYGRVW-CCLGR-1' })]);
		}).finally(()=>{
			setLoading(false);
		})
	}

	var downloadPoliza = ()=>{
		var date_completed : number = null;
		var payouts : number[] = [];
		if(!!group && !!group.payouts){
			for(var i of group.payouts){
				if(i.completed){
					if(!date_completed) date_completed = i.date_completed;
					else date_completed = Math.max(i.date_completed, date_completed);
					payouts.push(i.payout_id);
				}
			}
		}
		if(!group?.payouts || payouts.length!==group?.payouts.length) return Toast.error(t('payout_group.not_completed'));
		var tix = Toast.show(t('payout_group.downloading_poliza'), {
			loading: true,
			dismissable: false,
		});

		API.downloadPayoutPoliza(payouts, group.group_id, date_completed).then(res=>{
			if(res.error) return Toast.error(res.message);
		}).catch(err=>{
			if(DEBUG) console.log(err);
			Toast.error(t('common.unexpected_code', { code: 'LCL-PYGRDWPL-1' }));
		}).finally(()=>{
			Toast.dismiss(tix);
		})
	}

	var onAssetDeleted = (asset_id: number)=>{
		if(!payoutAssets) return;
		setPayoutAssets(p=>p.filter(a=>a.asset_id!==asset_id));
		var payout_id = shownPayout || null;
		if(!!payout_id){
			var py = [...group.payouts];
			var px = py.findIndex(a=>a.payout_id===payout_id);
			if(px!=-1){
				py[px].asset_count = Math.max(py[px].asset_count-1, 0);
			}
			setGroup(g=>({
				...g,
				payouts: py
			}));
		}
		setDeletingAsset(null);
		Toast.success(t('toasts.asset_deleted'))
	}

	var onAssetUploaded = (file: AssetFile)=>{
		if(!payoutAssets) return;
		setPayoutAssets(p=>[
			...p,
			file
		]);

		var payout_id = shownPayout || null;
		if(!!payout_id){
			var py = [...group.payouts];
			var px = py.findIndex(a=>a.payout_id===payout_id);
			if(px!=-1){
				py[px].asset_count = Math.max(py[px].asset_count+1, 0);
			}
			setGroup(g=>({
				...g,
				payouts: py
			}));
		}
	}

	var group_amount = useMemo(()=>{
		if(!group) return 0;
		return group.payouts.reduce((a,b)=>a+b.dates.reduce((c, d)=>c+d.amount, 0), 0)
	}, [group]);

	var { completed, cancelled } = useMemo(()=>{
		if(!group) return { completed: false, cancelled: false }
		var completed = true, cancelled = true;
		for(var i of group.payouts){
			if(!i.cancelled && !i.rejected) cancelled = false;
			if(!i.completed && !(i.cancelled || i.rejected)){
				completed = false;
			}
		}
		return { completed, cancelled };
	}, [group, group?.payouts]);

	var payout_status = useMemo(()=>{
		if(!group) return null;
		return <Label color={group.deleted ? 'red' : (!group.approved ? 'orange' : (cancelled ? 'red' : (completed ? 'green' : 'blue')))}>
			{group.deleted ? 'Eliminado' : (!group.approved ? 'No aprobado' : (cancelled ? 'Cancelado' : (completed ? 'Completado' : 'Pendiente')))}
		</Label>
	}, [completed, cancelled, group]);

	var groupped_deposits = useMemo(()=>{
		if(!group || group?.payouts.length==0) return [];
		var payouts = group.payouts.filter(a=>!a.completed && !a.cancelled && !a.rejected && !!a.bank_account)
		var act = uniqueArray(payouts.map(a=>a.bank_account.account_id));
		if(act.length==0) return [];
		var deposits : GrouppedDeposit[] = [];
		for(var i of act){
			var account_payouts = payouts.filter(a=>a.bank_account.account_id==i);
			if(account_payouts.length==0) continue;
			var account = account_payouts[0].bank_account;
			var dep : GrouppedDeposit = {
				account_id: i,
				account_type: account.clabe ? 'SPEI' : 'card',
				email: account.email_account,
				account_name: account.name_account,
				account_number: account.clabe || account.card_number,
				curp: account.curp,
				rfc: account.rfc,
				zipcode: account.postal_code,
				bank_name: account.bank,
				amount: 0,
				payouts: account_payouts.map(a=>({
					payout_id: a.payout_id,
					event_name: a.dates.map(b=>b.event_name).join(', '),
					amount: a.dates.reduce((b,c)=>b+c.amount, 0),
				})),
			}

			for(var p of account_payouts){
				var amount = p.dates.reduce((a,b)=>a+b.amount, 0);
				dep.amount += amount;
			}
			deposits.push(dep);
		}
		return deposits;
	}, [group, group?.payouts]);

	var shown_deposit = useMemo(()=>{
		if(!shownDeposit) return null;
		return groupped_deposits.find(a=>a.account_id===shownDeposit);
	}, [shownDeposit, groupped_deposits]);

	var shown_payout = useMemo(()=>{
		if(!shownPayout || !group || !group.payouts || group.payouts.length==0) return null;
		return group.payouts.find(a=>a.payout_id===shownPayout);
	}, [shownPayout, group]);

	if(!id || Number.isNaN(parseInt(id))){
		return <NotFound />
	}
	if(loadError){
		return <Header text={t('common.error')} subtext={loadError} />
	}
	if(!group){
		return <Header text={t('common.loading')} loading />
	}

	return <div className='ar payout-group-view-screen'>
		<Header text={t('payout_group.title', { id })} />
		{!!group.deleted && (
			<Message type='error' style={{ maxWidth: 400, margin: 'auto' }} centered header={t('payout_group.group_rejected')}>
				{t('payout_group.group_rejected_text')}
				<Message style={{ marginTop: 10 }}>
					{group.feedback || <i style={{ color: 'gray' }}>{t('payout_group.no_feedback')}</i>}
				</Message>
			</Message>
		)}
		<Groupper fitted title='Detalles' style={{ maxWidth: 400, margin: 'auto', marginTop: 15 }}>
			{!!completed && (
				<Toolbar fitted>
					<Toolbar.Dropdown iconName='file-download' text='Descargar' items={[
						{ text: 'Poliza', iconName: 'file-text', onClick: downloadPoliza }
					]} />
				</Toolbar>
			)}
			<Table details striped>
				<Table.Body
					data={[
						['ID', id],
						[t('common.payouts'), group.payouts.length],
						[t('common.status'), payout_status],
						[],
						[t('common.bank'), group.bank_name],
						[t('common.scheduled'), fromUnixLocal(group.date_scheduled).toFormat('dd/MMM/yyyy')],
						[t('common.amount'), <div style={{ fontSize: 20, fontWeight: 'bold' }}>
							${addCommas(group_amount)}
						</div>],
					]}
				/>
				{(group.deleted || groupped_deposits.length==0) ? null : !group.approved ? (
					hasAccess(UserAccess.CONTABILIDAD.APPROVE_PAYOUT_GROUP) ? <Table.Footer actions={<>
						<Button text='Rechazar' color='red' onClick={bindClick(setApproveStatus, 'reject')} />
						<Button text='Aprobar' color='green' onClick={bindClick(setApproveStatus, 'approve')} />
					</>} /> : null
				) : (group.bank_type===BusinessBankType.STP && hasAccess(UserAccess.CONTABILIDAD.DOWNLOAD_STP_FILE)) ? (
					<Table.Footer actions={(
						<Button text={t('common.download')} color='black' iconName='file-text' onClick={showSTPDownload(group.payouts.map(a=>a.payout_id))} />
					)} />
				) : null}
			</Table>
		</Groupper>
		{!group.deleted && groupped_deposits.length>0 && (
			<Table striped style={{ maxWidth: 700, margin: 'auto', marginTop: 15 }}>
				<Table.Head
					collapsingIndexes={[1, 2, 3]}
					centeredIndexes={[2]}
					title={t('payout_group.deposits')}
					data={[[t('common.bank'), t('common.type'), t('common.payouts'), t('common.amount')]]}
				/>
				<Table.Body>
					{groupped_deposits.map(a=>(
						<Table.Row selectable
							key={`DPST-${a.account_id}`}
							collapsingIndexes={[1, 2, 3]}
							centeredIndexes={[2]}
							onClick={showDeposit(a.account_id)}
							data={[
								`${a.bank_name} **${a.account_number?.slice(-4)}`,
								<Label color={a.account_type=='SPEI' ? 'blue' : 'orange'}>
									<Icon name={a.account_type=='SPEI' ? 'institution' : 'credit-card'} />
									{a.account_type=='SPEI' ? 'SPEI' : 'Tarjeta'}
								</Label>,
								a.payouts.length,
								<b>${addCommas(a.amount)}</b>
							]}
						/>
					))}
				</Table.Body>
			</Table>
		)}
		<Table striped
			style={{ maxWidth: 700, margin: 'auto', marginTop: 15 }}
			>
			<Table.Head
				title={t('common.payouts')}
				data={[[t('common.id'), t('common.event'), t('common.type'), t('common.status'), t('common.amount'), <Icon name='file-pdf' />, <Icon name='ellipsis-h' />]]}
				collapsingIndexes={[0, 2, 3, 4, 6]}
				centeredIndexes={[5, 6]}
			/>
			<Table.Body>
				{group.payouts.map(a=>(
					<Table.Row
						key={`PYFO-${a.payout_id}`}
						collapsingIndexes={[0, 2, 3, 4, 5]}
						// onClick={showPayout(a.payout_id)}
						data={[
							a.payout_id,
							uniqueArray(a.dates.map(a=>a.event_name)).join(', '),
							a.payout_type_name,
							<Label color={(a.cancelled || a.rejected) ? 'red' : (a.completed ? 'green' : 'blue')}>
								{a.cancelled ? 'Cancelada' : (a.completed ? 'Completada' : (a.rejected ? 'Rechazada' : 'Pendiente'))}
							</Label>,
							<b>${addCommas(a.dates.reduce((a,b)=>a+b.amount, 0))}</b>,
							<div style={{ cursor: 'pointer' }} onClick={showAssets(a.payout_id)}>
								<Icon name='file-pdf' />
								{a.asset_count<=0 ? (
									<Icon name='times' color='gray' style={{ marginRight: 0 }} />
								) : (
									<span style={{ display: 'inline-block', minWidth: 15, textAlign: 'center' }}>{a.asset_count}</span>
								)}
							</div>,
							<Button size='tiny' icon iconName='ellipsis-h' menu={[
								{ text: t('payout_group.view_payout'), onClick: showPayout(a.payout_id) },
								{ text: t('payout_group.view_evidence'), onClick: showAssets(a.payout_id) },
							]} />
						]}
					/>
				))}
			</Table.Body>
		</Table>
		<UserLog user={group} />
		{!group.deleted && groupped_deposits.length>0 && (
			<div style={{ marginTop: 15 }}>
				<Button basic text={t('payout_group.cancel')} color='red' style={{ display: 'block', margin: 'auto', maxWidth: 200 }} onClick={bindClick(setShownModal, Modals.CANCEL_GROUP)} />
			</div>
		)}

		<Modal open={!!approveStatus} onClose={bindClick(setApproveStatus)} size='mini'>
			<Modal.Header>{t(`payout_group.${approveStatus}`)}</Modal.Header>
			<Modal.Content>
				<Header text={t(`payout_group.${approveStatus}_confirm`)} />
				<Message type='info' centered>
					{t(`payout_group.${approveStatus}_message`)}
				</Message>
				{approveStatus==='reject' && (
					<Input label={t('payout_group.reject_reason')} style={{ marginTop: 10 }} textarea value={rejectFeedback} onChange={setRejectFeedback} />
				)}
				<Message list={modalPrompts} type='error' style={{ marginTop: 10 }} />
			</Modal.Content>
			<Modal.Actions>
				<Button text={t('common.close')} basic onClick={bindClick(setApproveStatus)} />
				<Button text={t(`common.${approveStatus}`)} color={approveStatus==='approve' ? 'green' : 'red'} onClick={sendApproval} />
			</Modal.Actions>
		</Modal>
		<Modal open={shownModal===Modals.DEPOSIT_INFO} onClose={bindClick(setShownModal)} size='tiny'>
			<Modal.Header>{t('payout_group.deposit_info.title')}</Modal.Header>
			{!!shown_deposit && <Modal.Content>
				<Table fitted details>
					<Table.Body
						data={[
							['ID', shown_deposit.account_id],
							['Correo', shown_deposit.email],
							['Nombre cuenta', shown_deposit.account_name],
							...(shown_deposit.rfc ? [
								['RFC', shown_deposit.rfc],
							] : []),
							...(shown_deposit.curp ? [
								['CURP', shown_deposit.curp],
							] : []),
							...(shown_deposit.zipcode ? [
								['Código postal', shown_deposit.zipcode],
							] : []),
							[],
							['Banco', shown_deposit.bank_name],
							['Cuenta', <>
								<Icon name={shown_deposit.account_type==='card' ? 'credit-card' : 'landmark'} />
								{dashify(shown_deposit.account_number, 4, ' ')}
							</>],
							[],
							['Cantidad', <div style={{ fontSize: 18, fontWeight: 'bold' }}>${addCommas(shown_deposit.amount)}</div>]
						]}
					/>
				</Table>
				<Table fitted className='last' style={{ marginTop: 0 }}>
					<Table.Head
						data={[['ID', 'Evento', 'Cantidad']]}
						collapsingIndexes={[0, 2]}
					/>
					<Table.Body>
						{shown_deposit.payouts.map(a=>(
							<Table.Row 
								key={`SDP-${a.payout_id}`} 
								collapsingIndexes={[0, 2]} 
								data={[a.payout_id, a.event_name, `$${addCommas(a.amount)}`]} 
							/>
						))}
					</Table.Body>
				</Table>
				<Message type='error' list={modalPrompts} style={{ marginTop: 15 }} />
			</Modal.Content>}
			<Modal.Actions>
				<Button text={t('common.close')} basic onClick={bindClick(setShownModal)} />
				{group?.bank_type!==BusinessBankType.STP && (
					<Button color='black' text={t('payout_group.complete')} onClick={depositCompleted(shown_deposit?.account_id)} />
				)}
			</Modal.Actions>
		</Modal>
		<Modal open={shownModal===Modals.PAYOUT_INFO} size='tiny' onClose={bindClick(setShownModal)}>
			<Modal.Header>{t('payout_group.payout_info.title')}</Modal.Header>
			{!!shown_payout && <Modal.Content>
				<Table fitted striped details>
					<Table.Head />
					<Table.Body data={[
						['ID', shown_payout.payout_id],
						[t('common.event'), uniqueArray(shown_payout.dates.map(a=>a.event_name)).join(', ')],
						[t('common.type'), shown_payout.payout_type_name],
						[t('common.status'), (
							<Label color={shown_payout.cancelled ? 'red' : (shown_payout.completed ? 'green' : (shown_payout.approved ? 'orange' : 'blue'))}>
								{
									shown_payout.cancelled ? 'Cancelada' : 
									shown_payout.completed ? 'Completada' : 
									shown_payout.approved ? 'Pendiente' : 
									'No aprobada'
								}
							</Label>
						)],
						[],
						['Correo', shown_payout.bank_account?.email_account],
						['Nombre cuenta', shown_payout.bank_account?.name_account],
						...(shown_payout.bank_account?.rfc ? [
							['RFC', shown_payout.bank_account?.rfc],
						] : []),
						...(shown_payout.bank_account?.curp ? [
							['CURP', shown_payout.bank_account?.curp],
						] : []),
						...(shown_payout.bank_account?.postal_code ? [
							['Código postal', shown_payout.bank_account?.postal_code],
						] : []),
						[],
						['Banco', shown_payout.bank_account?.bank],
						['Cuenta', <>
							<Icon name={!!shown_payout.bank_account?.card_number ? 'credit-card' : 'landmark'} />
							{dashify((shown_payout.bank_account?.card_number || shown_payout.bank_account?.clabe), 4, ' ')}
						</>],
						[],
						['Cantidad', <div style={{ fontSize: 18, fontWeight: 'bold' }}>${addCommas(shown_payout.dates.reduce((a,b)=>a+b.amount, 0))}</div>]
					]} />
				</Table>
				<Table fitted className='last' style={{ marginTop: 0 }}>
					<Table.Head data={[['ID', 'Fecha', 'Cantidad']]} collapsingIndexes={[0, 2]} />
					<Table.Body
						data={shown_payout.dates.map(a=>(
							[a.date_id, fromUnixLocal(a.date).toFormat('dd/MMM/yyyy HH:mm'), `$${addCommas(a.amount)}`]
						))}
					/>
				</Table>
			</Modal.Content>}
			<Modal.Actions>
				<Button text={t('common.close')} basic onClick={bindClick(setShownModal)} />
				{!group.deleted && group?.bank_type!==BusinessBankType.STP && !!shown_payout && !shown_payout.completed && !shown_payout.cancelled && shown_payout.approved && (
					<Button color='black' text={t('payout_group.complete')} onClick={payoutsCompleted([shown_payout.payout_id])} />
				)}
			</Modal.Actions>
		</Modal>
		<Modal open={shownModal===Modals.PAYOUT_ASSETS} size='small' onClose={bindClick(setShownModal)}>
			<Modal.Header>{t('payout_group.assets_info.title')}</Modal.Header>
			{!!shown_payout && <Modal.Content>
				<Table fitted details>
					<Table.Body data={[
						['ID', shown_payout.payout_id],
						[t('common.event'), uniqueArray(shown_payout.dates.map(a=>a.event_name)).join(', ')],
						[],
						['Nombre cuenta', shown_payout.bank_account?.name_account],
						['Banco', shown_payout.bank_account?.bank],
						['Cuenta', <>
							<Icon name={!!shown_payout.bank_account?.card_number ? 'credit-card' : 'landmark'} />
							{dashify((shown_payout.bank_account?.card_number || shown_payout.bank_account?.clabe), 4, ' ')}
						</>],
						[],
						['Cantidad', <div style={{ fontSize: 18, fontWeight: 'bold' }}>${addCommas(shown_payout.dates.reduce((a,b)=>a+b.amount, 0))}</div>]
					]} />
				</Table>
				<Table fitted className='last' style={{ marginTop: 0 }} >
					<Table.Head
						data={[['', 'Archivo', 'Fecha', <Icon name='ellipsis-h' />]]}
						centeredIndexes={[3]}
						title='Archivos'
						titleSize='small'
						titleRight={hasAccess(UserAccess.CONTABILIDAD.UPLOAD_PAYOUT_FILE) && (
							<div style={{ textAlign: 'center' }}>
								<UploadAssetButton
									size='tiny'
									onUpload={onAssetUploaded}
									typeId={AssetType.PDF}
									data={{
										payouts: shownPayout ? [shownPayout] : []
									}}
								/>
							</div>	
						)}
					/>
					<Table.Body>
						{(modalPrompts) ? (
							<Table.Cell row colSpan={100}>
								<Header text={t('common.error')} subtext={modalPrompts[0]} />
							</Table.Cell>
						) : !payoutAssets ? (
							<Table.Cell row colSpan={100}>
								<Header loading text={t('common.loading')} size='small' style={{ margin: 0, padding: 10 }} textStyle={{ fontSize: 16 }} />
							</Table.Cell>
						) : payoutAssets.length==0 ? (
							<Table.Cell empty row colSpan={100}>
								{t('payout_group.assets_info.empty')}
							</Table.Cell>
						) : payoutAssets.map(a=>(
							<Table.Row key={`AST-${a.asset_id}`}>
								<Table.Cell collapsing>
									<Icon name='file-pdf' style={{ marginRight: 0 }} />
								</Table.Cell>
								<Table.Cell>{a.asset_name}</Table.Cell>
								<Table.Cell collapsing>{fromUnixLocal(a.date_created).toFormat('dd/MMM/yy HH:mm')}</Table.Cell>
								<Table.Cell collapsing>
									<Button size='tiny' icon iconName='ellipsis-h' menu={[
										{ text: t('payout_view.assets.view_asset'), as: Link, to: a.download_url, target: '_blank' },
										{ text: t('payout_view.assets.delete_asset'), onClick: bindClick(setDeletingAsset, a) },
									]} />
								</Table.Cell>
							</Table.Row>
						))}
					</Table.Body>
				</Table>
			</Modal.Content>}
			<Modal.Actions>
				<Button text={t('common.close')} basic onClick={bindClick(setShownModal)} />
				{!group.deleted && group?.bank_type!==BusinessBankType.STP && !!shown_payout && !shown_payout.completed && !shown_payout.cancelled && shown_payout.approved && (
					<Button color='black' text={t('payout_group.complete')} onClick={payoutsCompleted([shown_payout.payout_id])} />
				)}
			</Modal.Actions>
		</Modal>
		<Modal open={shownModal===Modals.CANCEL_GROUP} onClose={bindClick(setShownModal)} size='tiny'>
			<Modal.Header>{t('payout_group.cancel')}</Modal.Header>
			<Modal.Content>
				{!hasAccess(UserAccess.CONTABILIDAD.CANCEL_PAYOUT_GROUP) ? (
					<NoAccessMessage />
				) : <>
					<Header text={t('payout_group.cancel')} />
					<Message centered type='error'>
						<Trans i18nKey={t('payout_group.reject_message')} />
					</Message>
				</>}
				<Message type='error' list={modalPrompts} style={{ marginTop: 15 }} />
			</Modal.Content>
			<Modal.Actions>
				<Button text={t('common.close')} basic onClick={bindClick(setShownModal)} />
				{hasAccess(UserAccess.CONTABILIDAD.CANCEL_PAYOUT_GROUP) && (
					<Button text={t('common.cancel')} color='black' onClick={cancelGroup} />
				)}
			</Modal.Actions>
		</Modal>
		<STPDownloadModal
			open={!!downloadPayouts && downloadPayouts.length>0}
			payouts={downloadPayouts}
			onPayoutsCompleted={onPayoutsCompleted}
			onClose={bindClick(setDownloadPayouts, null)}
		/>
		<DeleteAssetModal
			open={!!deletingAsset}
			asset={deletingAsset}
			onDeleted={onAssetDeleted}
			onClose={bindClick(setDeletingAsset)}
			data={{
				payouts: shownPayout ? [shownPayout] : []
			}}
			tableData={[
				[t('common.payout'), shownPayout],
			]}
		/>
	</div>
}

export default PayoutGroupView;